/**
 * useGridState Hook
 *
 * Custom hook for managing MUI DataGrid state persistence.
 * Handles:
 * - Filter model (sessionStorage)
 * - Sort model (sessionStorage)
 * - Pagination model (sessionStorage)
 * - Column visibility (localStorage)
 */

import { useState, useCallback } from 'react'

/**
 * Custom hook for managing grid state with persistence
 *
 * @param {string} storageKey - Base key for sessionStorage (e.g., 'employeeListGridState')
 * @param {string} columnVisibilityKey - Key for localStorage column visibility
 * @param {Object} defaultColumnVisibility - Default column visibility model
 * @returns {Object} Grid state and handlers
 */
export const useGridState = (storageKey, columnVisibilityKey, defaultColumnVisibility = {}) => {
  // Helper function to get stored grid state
  const getStoredGridState = useCallback(() => {
    const baseState = {
      filterModel: { items: [], linkOperator: 'and' },
      sortModel: [],
      paginationModel: { page: 0, pageSize: 25 },
      columnVisibilityModel: defaultColumnVisibility
    }

    try {
      const stored = sessionStorage.getItem(storageKey)
      if (stored) {
        const sessionState = JSON.parse(stored)
        Object.assign(baseState, sessionState)
      }
    } catch (error) {
      // Use defaults if parsing fails
    }

    try {
      const storedColumnVisibility = localStorage.getItem(columnVisibilityKey)
      if (storedColumnVisibility) {
        baseState.columnVisibilityModel = JSON.parse(storedColumnVisibility)
      }
    } catch (error) {
      // Use defaults if parsing fails
    }

    return baseState
  }, [storageKey, columnVisibilityKey, defaultColumnVisibility])

  const storedState = getStoredGridState()

  // State management
  const [filterModel, setFilterModel] = useState(storedState.filterModel)
  const [sortModel, setSortModel] = useState(storedState.sortModel)
  const [paginationModel, setPaginationModel] = useState(storedState.paginationModel)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(storedState.columnVisibilityModel)

  // Helper function to save grid state to sessionStorage
  const saveGridState = useCallback((updates = {}) => {
    // Read current state from sessionStorage to avoid stale closure values
    const stored = sessionStorage.getItem(storageKey)
    const currentState = stored ? JSON.parse(stored) : {
      filterModel: { items: [], linkOperator: 'and' },
      sortModel: [],
      paginationModel: { page: 0, pageSize: 25 }
    }

    // Merge updates
    const newState = {
      ...currentState,
      ...updates
    }

    sessionStorage.setItem(storageKey, JSON.stringify(newState))
  }, [storageKey])

  // Helper function to save column visibility to localStorage
  const saveColumnVisibility = useCallback((columnVisibility) => {
    localStorage.setItem(columnVisibilityKey, JSON.stringify(columnVisibility))
  }, [columnVisibilityKey])

  // Handler for filter model changes
  const handleFilterModelChange = useCallback((model, callback) => {
    setFilterModel(model)
    saveGridState({ filterModel: model })
    if (callback) callback(model)
  }, [saveGridState])

  // Handler for sort model changes
  const handleSortModelChange = useCallback((model, callback) => {
    setSortModel(model)
    saveGridState({ sortModel: model })
    if (callback) callback(model)
  }, [saveGridState])

  // Handler for pagination model changes
  const handlePaginationModelChange = useCallback((model, callback) => {
    setPaginationModel(model)
    saveGridState({ paginationModel: model })
    if (callback) callback(model)
  }, [saveGridState])

  // Handler for column visibility changes
  const handleColumnVisibilityModelChange = useCallback((model, callback) => {
    setColumnVisibilityModel(model)
    saveColumnVisibility(model)
    if (callback) callback(model)
  }, [saveColumnVisibility])

  return {
    // State
    filterModel,
    sortModel,
    paginationModel,
    columnVisibilityModel,
    // Setters (for direct updates)
    setFilterModel,
    setSortModel,
    setPaginationModel,
    setColumnVisibilityModel,
    // Handlers (for grid events)
    handleFilterModelChange,
    handleSortModelChange,
    handlePaginationModelChange,
    handleColumnVisibilityModelChange,
    // Storage helpers
    saveGridState,
    saveColumnVisibility
  }
}
